import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { FileText, Table2, Upload, Trash2, Link2, ChevronDown, ChevronUp, RefreshCw } from "lucide-react";
import { toast } from "sonner";

/** Templates ("formatos de origen") the school copies when creating a new document. */
export const NotebookTemplates = () => {
  const { user } = useAuth();
  const canManage = ["superadmin", "academic_director"].includes(user?.role);
  const [open, setOpen] = useState(false);
  const [rows, setRows] = useState([]);
  const [url, setUrl] = useState("");
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);

  const load = () => api.get("/notebook/templates").then((r) => setRows(r.data || []));
  useEffect(() => { load(); }, []);

  const upload = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setBusy(true);
    const fd = new FormData();
    fd.append("file", f);
    try {
      await api.post("/notebook/templates/upload", fd);
      toast.success("Formato guardado");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No se pudo subir");
    } finally {
      setBusy(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const rescan = async () => {
    setBusy(true);
    try {
      const r = await api.post("/notebook/templates/rescan");
      setRows(r.data.templates || []);
      toast.success(r.data.added
        ? `Recuperamos ${r.data.added} plantilla(s) de tu Drive`
        : "No hay plantillas nuevas en la carpeta de Drive");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No se pudo revisar Drive");
    } finally { setBusy(false); }
  };

  const link = async () => {
    if (!url.trim()) return;
    setBusy(true);
    try {
      await api.post("/notebook/templates/link", { url });
      setUrl("");
      toast.success("Formato guardado");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo guardar");
    } finally { setBusy(false); }
  };

  const remove = async (tp) => {
    if (!window.confirm(`¿Quitar el formato "${tp.name}"? El documento sigue en Drive.`)) return;
    await api.delete(`/notebook/templates/${tp.id}`);
    load();
  };

  return (
    <Card className="p-4 mb-5" data-testid="notebook-templates-card">
      <button
        type="button"
        className="flex items-center gap-2 text-sm font-semibold text-slate-800 w-full text-left"
        onClick={() => setOpen((o) => !o)}
        data-testid="templates-toggle"
      >
        {open ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
        Formatos de origen ({rows.length})
      </button>

      {open && (
        <div className="mt-4 space-y-4">
          <p className="text-xs text-slate-500">
            Un formato es un documento base (ej. "Documento de clase"). Al crear un documento nuevo podés
            elegirlo y la app hace una copia limpia dentro de la carpeta de la clase.
          </p>

          {!canManage && (
            <p className="text-xs text-slate-500" data-testid="templates-readonly-note">
              Los formatos los administran la dirección académica y el admin. Vos podés usarlos al
              crear un documento nuevo.
            </p>
          )}

          {canManage && (
          <div className="flex flex-wrap items-center gap-2">
            <Button size="sm" variant="outline" onClick={() => fileRef.current?.click()} disabled={busy} data-testid="template-upload">
              <Upload className="h-4 w-4" /> Subir Word o Excel
            </Button>
            <Button size="sm" variant="outline" onClick={rescan} disabled={busy} data-testid="template-rescan">
              <RefreshCw className="h-4 w-4" /> Buscar en Drive
            </Button>
            <input
              ref={fileRef}
              type="file"
              accept=".docx,.doc,.xlsx,.xls"
              className="hidden"
              onChange={upload}
              data-testid="template-file-input"
            />
            <span className="text-xs text-slate-400">o</span>
            <Input
              className="max-w-md"
              placeholder="Pegá el enlace de un Documento de Google de la escuela"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              data-testid="template-link-input"
            />
            <Button size="sm" onClick={link} disabled={busy || !url.trim()} data-testid="template-link-save">
              <Link2 className="h-4 w-4" /> Guardar enlace
            </Button>
          </div>
          )}

          {rows.length > 0 && (
            <div className="divide-y divide-slate-100" data-testid="templates-list">
              {rows.map((tp) => {
                const Icon = tp.kind === "sheet" ? Table2 : FileText;
                return (
                  <div key={tp.id} className="py-2 flex items-center gap-3" data-testid={`template-row-${tp.id}`}>
                    <Icon className="h-4 w-4 text-slate-500 shrink-0" />
                    <a href={tp.web_link} target="_blank" rel="noopener noreferrer" className="text-sm text-slate-800 hover:text-brand truncate flex-1">
                      {tp.name}
                    </a>
                    {canManage && (
                      <Button size="icon" variant="ghost" className="h-8 w-8 text-red-600" onClick={() => remove(tp)} data-testid={`template-delete-${tp.id}`}>
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </Card>
  );
};

export default NotebookTemplates;
